import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TextInput } from "flowbite-react";
import { toast } from "react-toastify";
import {
  HiOutlineTrash,
  HiChevronUp,
  HiChevronDown,
  HiOutlinePlus,
} from "react-icons/hi";
import { CustomButton } from "../../../atoms/CustomButton";
import { ModalShell } from "../../../atoms/ModalShell";
import { CharacterCounter } from "../../../atoms/CharacterCounter";
import useAuth from "../../../../hooks/useAuth";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import { useSaveLeadColumns } from "../../../../hooks/leads";
import {
  LeadColumn,
  MAX_LEAD_COLUMNS,
  MAX_COLUMN_LABEL_LENGTH,
} from "../../../../types/LeadTypes";
import { columnLabel } from "./leadColumns";

interface Props {
  show: boolean;
  onClose: () => void;
  columns: LeadColumn[];
}

const ColumnsConfigModal: React.FC<Props> = ({ show, onClose, columns }) => {
  const { t } = useTranslation();
  const { user, customer_identification } = useAuth();
  const permissions = usePermissions(user);
  const canWrite = permissions.can(Cap.LEADS, Level.WRITE);
  const save = useSaveLeadColumns(
    customer_identification,
    user.entity_identification
  );

  const [draft, setDraft] = useState<LeadColumn[]>([]);

  useEffect(() => {
    if (show) {
      setDraft(columns.map((c) => ({ id: c.id, label: columnLabel(c, t) })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, columns]);

  const setLabel = (index: number, label: string) =>
    setDraft((d) => d.map((c, i) => (i === index ? { ...c, label } : c)));

  const remove = (index: number) =>
    setDraft((d) => (d.length <= 1 ? d : d.filter((_, i) => i !== index)));

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= draft.length) return;
    setDraft((d) => {
      const next = [...d];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const add = () => {
    if (draft.length >= MAX_LEAD_COLUMNS) return;
    setDraft((d) => [...d, { id: "", label: "" }]);
  };

  const handleSave = () => {
    const cleaned = draft.map((c) => ({ id: c.id, label: c.label.trim() }));
    if (cleaned.some((c) => !c.label)) {
      toast.error(t("leads.columns.labelRequired") as string);
      return;
    }
    save.mutate(cleaned as LeadColumn[], {
      onSuccess: (res) => {
        const reassigned = res?.data?.element?.reassigned ?? 0;
        toast.success(
          reassigned > 0
            ? (t("leads.columns.savedReassigned", {
                count: reassigned,
              }) as string)
            : (t("leads.columns.saved") as string)
        );
        onClose();
      },
      onError: (err) => {
        toast.error(
          (err?.response?.data?.message ||
            t("leads.columns.saveError")) as string
        );
      },
    });
  };

  return (
    <ModalShell
      show={show}
      onClose={onClose}
      size="md"
      dismissible
      title={t("leads.columns.title")}
      footer={
        <>
          <CustomButton
            variant="secondary"
            title={t("common.cancel") as string}
            onClick={onClose}
          />
          <CustomButton
            variant="primary"
            isLoading={save.isLoading}
            disabled={save.isLoading || !canWrite}
            title={t("common.saveShort") as string}
            onClick={handleSave}
          />
        </>
      }
    >
      <div className="space-y-2">
        {draft.map((col, index) => (
          <div key={index} className="flex items-start gap-2">
            <div className="flex flex-col">
              <CustomButton
                variant="none"
                size="icon"
                onClick={() => move(index, -1)}
                disabled={index === 0 || !canWrite}
                className="text-gray-400 enabled:hover:text-gray-700"
                icon={<HiChevronUp size={16} />}
                aria-label={t("common.moveUp") as string}
              />
              <CustomButton
                variant="none"
                size="icon"
                onClick={() => move(index, 1)}
                disabled={index === draft.length - 1 || !canWrite}
                className="text-gray-400 enabled:hover:text-gray-700"
                icon={<HiChevronDown size={16} />}
                aria-label={t("common.moveDown") as string}
              />
            </div>
            <div className="flex-1 min-w-0">
              <TextInput
                type="text"
                value={col.label}
                maxLength={MAX_COLUMN_LABEL_LENGTH}
                disabled={!canWrite}
                placeholder={t("leads.columns.columnPlaceholder") as string}
                onChange={(e) => setLabel(index, e.target.value)}
                className="w-full"
              />
              <CharacterCounter
                currentLength={col.label.length}
                maxLength={MAX_COLUMN_LABEL_LENGTH}
                className="mt-0.5"
              />
            </div>
            <CustomButton
              variant="none"
              size="icon"
              onClick={() => remove(index)}
              disabled={draft.length <= 1 || !canWrite}
              className="text-gray-400 enabled:hover:text-red-600"
              icon={<HiOutlineTrash size={18} />}
              aria-label={t("leads.columns.remove") as string}
            />
          </div>
        ))}
      </div>

      {draft.length < MAX_LEAD_COLUMNS && (
        <CustomButton
          variant="none"
          size="sm"
          onClick={add}
          disabled={!canWrite}
          className="mt-3 inline-flex gap-1.5 px-0 text-blue-600 enabled:hover:text-blue-800"
          icon={<HiOutlinePlus size={16} />}
          title={t("common.add") as string}
        />
      )}
    </ModalShell>
  );
};

export default ColumnsConfigModal;
